import React, { useState, useEffect } from 'react';
import {
  X,
  Phone,
  PhoneMissed,
  Clock,
  Trash2,
  PhoneCall,
  Search,
  Filter,
  RefreshCw,
  UserPlus,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { karelMultiUserSyncService } from '../../services/karelMultiUserSync';
import { karelApiService } from '../../services/karelApi';
import { useAuth } from '../../contexts/AuthContext';

interface MissedCall {
  id: string;
  call_id: string;
  call_phone_number: string;
  call_start_date: string;
  diagram_name: string;
  status: string;
  press_keys?: boolean;
  ring_agent?: boolean;
  missed_call_return_status?: string;
  notes?: string;
  lead_id?: string;
  assigned_to?: string;
  leads?: {
    first_name: string;
    last_name: string;
  };
  profiles?: {
    name: string;
  };
}

interface MissedCallsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MissedCallsModal: React.FC<MissedCallsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const [missedCalls, setMissedCalls] = useState<MissedCall[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<MissedCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPeriod, setSyncPeriod] = useState<'24h' | '7d' | '30d' | '90d'>('24h');
  const [searchQuery, setSearchQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState<'today' | 'yesterday' | '7days' | 'custom'>('today');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [pageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const last24Hours = new Date();
  last24Hours.setHours(last24Hours.getHours() - 24);
  const now = new Date();

  const [startDate, setStartDate] = useState(format(last24Hours, "yyyy-MM-dd'T'HH:mm"));
  const [endDate, setEndDate] = useState(format(now, "yyyy-MM-dd'T'HH:mm"));
  const [onlyNotReturned, setOnlyNotReturned] = useState('hepsi');
  const [pressKeysFilter, setPressKeysFilter] = useState('hepsi');
  const [ringAgentFilter, setRingAgentFilter] = useState('hepsi');
  const [diagramName, setDiagramName] = useState('');

  const [availableDiagrams, setAvailableDiagrams] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchMissedCalls(true);
      fetchDiagrams();
    }
  }, [isOpen]);

  const fetchDiagrams = async () => {
    try {
      const { data, error } = await supabase
        .from('missed_calls')
        .select('diagram_name')
        .not('diagram_name', 'is', null);

      if (error) throw error;

      // Extract unique diagram names
      const uniqueDiagrams = Array.from(new Set(data?.map(d => d.diagram_name).filter(Boolean) || [])).sort();
      setAvailableDiagrams(uniqueDiagrams);
    } catch (error) {
      console.error('Error fetching diagrams:', error);
    }
  };

  useEffect(() => {
    if (currentPage > 1) {
      fetchMissedCalls(false);
    }
  }, [currentPage]);

  useEffect(() => {
    let filtered = [...missedCalls];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(call => {
        const phone = call.call_phone_number.replace(/\D/g, '');
        const fullName = call.leads
          ? `${call.leads.first_name} ${call.leads.last_name}`.toLowerCase()
          : '';
        return phone.includes(query) || fullName.includes(query);
      });
    }

    setFilteredCalls(filtered);
  }, [missedCalls, searchQuery]);

  const applyQuickFilter = (filter: 'today' | 'yesterday' | '7days' | 'custom') => {
    setQuickFilter(filter);
    const now = new Date();

    switch (filter) {
      case 'today': {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        setStartDate(format(today, "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(todayEnd, "yyyy-MM-dd'T'HH:mm"));
        break;
      }
      case 'yesterday': {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const yesterdayEnd = new Date();
        yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
        yesterdayEnd.setHours(23, 59, 59, 999);
        setStartDate(format(yesterday, "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(yesterdayEnd, "yyyy-MM-dd'T'HH:mm"));
        break;
      }
      case '7days': {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        setStartDate(format(sevenDaysAgo, "yyyy-MM-dd'T'HH:mm"));
        setEndDate(format(now, "yyyy-MM-dd'T'HH:mm"));
        break;
      }
    }
  };

  const fetchMissedCalls = async (resetPage: boolean = false) => {
    if (resetPage) {
      setCurrentPage(1);
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const page = resetPage ? 1 : currentPage;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let countQuery = supabase
        .from('missed_calls')
        .select('*', { count: 'exact', head: true });

      let dataQuery = supabase
        .from('missed_calls')
        .select(`
          *,
          leads(first_name, last_name),
          profiles(name)
        `)
        .order('call_start_date', { ascending: false })
        .range(from, to);

      if (startDate) {
        countQuery = countQuery.gte('call_start_date', new Date(startDate).toISOString());
        dataQuery = dataQuery.gte('call_start_date', new Date(startDate).toISOString());
      }

      if (endDate) {
        countQuery = countQuery.lte('call_start_date', new Date(endDate).toISOString());
        dataQuery = dataQuery.lte('call_start_date', new Date(endDate).toISOString());
      }

      if (onlyNotReturned === 'hepsi') {
        countQuery = countQuery.or('missed_call_return_status.is.null,missed_call_return_status.eq.');
        dataQuery = dataQuery.or('missed_call_return_status.is.null,missed_call_return_status.eq.');
      }

      if (pressKeysFilter === 'aktif') {
        countQuery = countQuery.eq('press_keys', true);
        dataQuery = dataQuery.eq('press_keys', true);
      } else if (pressKeysFilter === 'pasif') {
        countQuery = countQuery.eq('press_keys', false);
        dataQuery = dataQuery.eq('press_keys', false);
      }

      if (ringAgentFilter === 'aktif') {
        countQuery = countQuery.eq('ring_agent', true);
        dataQuery = dataQuery.eq('ring_agent', true);
      } else if (ringAgentFilter === 'pasif') {
        countQuery = countQuery.eq('ring_agent', false);
        dataQuery = dataQuery.eq('ring_agent', false);
      }

      if (diagramName.trim()) {
        countQuery = countQuery.ilike('diagram_name', `%${diagramName.trim()}%`);
        dataQuery = dataQuery.ilike('diagram_name', `%${diagramName.trim()}%`);
      }

      const [{ count }, { data, error }] = await Promise.all([
        countQuery,
        dataQuery
      ]);

      if (error) throw error;

      if (resetPage) {
        setMissedCalls(data || []);
      } else {
        setMissedCalls(prev => [...prev, ...(data || [])]);
      }

      setHasMore(data && data.length === pageSize);
    } catch (error) {
      console.error('Error fetching missed calls:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleSync = async (onlyNotCallback: boolean = true) => {
    setSyncing(true);
    try {
      let result;

      switch (syncPeriod) {
        case '7d': {
          const end = new Date();
          const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
          result = await karelMultiUserSyncService.syncAllUsersMissedCalls(start, end, onlyNotCallback);
          break;
        }
        case '30d': {
          const end = new Date();
          const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
          result = await karelMultiUserSyncService.syncAllUsersMissedCalls(start, end, onlyNotCallback);
          break;
        }
        case '90d': {
          const end = new Date();
          const start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
          result = await karelMultiUserSyncService.syncAllUsersMissedCalls(start, end, onlyNotCallback);
          break;
        }
        default:
          result = await karelMultiUserSyncService.syncLast24Hours(onlyNotCallback);
      }

      if (result.success) {
        await fetchMissedCalls(true);

        const successUsers = result.userResults.filter(u => u.success);
        const failedUsers = result.userResults.filter(u => !u.success);

        let message = `Senkronizasyon tamamlandı!\n\n`;
        message += `✓ ${result.newCalls} yeni çağrı eklendi\n`;
        message += `✓ ${successUsers.length} kullanıcı başarılı\n`;
        message += `✓ ${result.totalProcessed} toplam çağrı işlendi\n`;

        if (failedUsers.length > 0) {
          message += `\n⚠️ ${failedUsers.length} kullanıcı başarısız:\n`;
          failedUsers.forEach(u => {
            message += `  - ${u.userName}: ${u.error}\n`;
          });
        }

        alert(message);
      } else {
        alert(`Senkronizasyon hatası:\n${result.errors.join('\n')}`);
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      alert(`Senkronizasyon hatası: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const cleanPhoneNumber = (phoneNumber: string): string => {
    let cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.startsWith('90')) {
      cleaned = '0' + cleaned.slice(2);
    } else if (!cleaned.startsWith('0') && cleaned.length === 10) {
      cleaned = '0' + cleaned;
    }

    return cleaned;
  };

  const handleCallBack = async (callId: string, phoneNumber: string) => {
    const cleanedNumber = cleanPhoneNumber(phoneNumber);

    if (window.makeKarelCall) {
      window.makeKarelCall(cleanedNumber);

      try {
        const { error } = await supabase
          .from('missed_calls')
          .update({
            status: 'called_back',
            updated_at: new Date().toISOString()
          })
          .eq('id', callId);

        if (!error) {
          await fetchMissedCalls(true);
        }
      } catch (error) {
        console.error('Error updating call status:', error);
      }
    } else {
      alert('Karel telefon sistemi bulunamadı. Lütfen Karel widget\'ının yüklendiğinden emin olun.');
    }
  };

  const handleConvertToLead = async (call: MissedCall) => {
    // 1. İsim sorma adımı
    let defaultFirstName = call.leads?.first_name || 'Cevapsız';
    let defaultLastName = call.leads?.last_name || 'Çağrı';

    const firstNameInput = window.prompt("Lead Adı:", defaultFirstName);
    if (firstNameInput === null) return; // İptal edildi

    const lastNameInput = window.prompt("Lead Soyadı:", defaultLastName);
    if (lastNameInput === null) return; // İptal edildi

    const finalFirstName = firstNameInput.trim() || defaultFirstName;
    const finalLastName = lastNameInput.trim() || defaultLastName;

    if (!await window.appConfirm(`${finalFirstName} ${finalLastName} adıyla lead oluşturulacak. Onaylıyor musunuz?`)) return;

    try {
      const phoneNumber = cleanPhoneNumber(call.call_phone_number);

      // Check for existing lead with this phone number.
      // Since 'phone' column is text, we use .eq() or .or() for flexibility.
      // We also handle the case where phone might be stored differently (with/without 0 prefix)
      // by relying on cleanPhoneNumber consistency or using ilike if needed.

      // Prepare alternative phone number formats for a robust check
      const phoneNumberWithoutZero = phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber;
      const phoneNumberWithCountryCode = `90${phoneNumberWithoutZero}`;
      const phoneNumberWithPlusCountryCode = `+90${phoneNumberWithoutZero}`;

      const { data: existingLeads, error: checkError } = await supabase
        .from('leads')
        .select('id, first_name, last_name, phone')
        .or(`phone.eq.${phoneNumber},phone.eq.${phoneNumberWithoutZero},phone.eq.${phoneNumberWithCountryCode},phone.eq.${phoneNumberWithPlusCountryCode}`);

      if (checkError) throw checkError;

      if (existingLeads && existingLeads.length > 0) {
        const lead = existingLeads[0];
        alert(`Bu numara ile kayıtlı bir lead zaten mevcut:\n${lead.first_name} ${lead.last_name}`);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('branch_id')
        .eq('id', user?.id)
        .single();

      const leadData = {
        first_name: finalFirstName,
        last_name: finalLastName,
        phone: phoneNumber, // Send as string, NOT array
        source: 'other',
        status: 'lead',
        tracking_status: 'new_lead',
        branch_id: profile?.branch_id,
        created_by: user?.id,
        notes: `Karel cevapsız çağrısından dönüştürüldü. Diyagram: ${call.diagram_name || 'Belirtilmemiş'}`
      };

      const { data: newLead, error: insertError } = await supabase
        .from('leads')
        .insert(leadData)
        .select()
        .single();

      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from('missed_calls')
        .update({
          status: 'converted_to_lead',
          lead_id: newLead.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', call.id);

      if (updateError) {
        console.error("Call status update failed", updateError);
        alert('Lead oluşturuldu ancak çağrı durumu güncellenirken hata oluştu.');
      }

      await fetchMissedCalls(true);
      if (!updateError) {
        alert('Çağrı başarıyla lead\'e dönüştürüldü.');
      }

    } catch (error: any) {
      console.error('Error converting to lead:', error);
      alert(`Lead oluşturulurken hata oluştu: ${error.message}`);
    }
  };

  const handleUpdateStatus = async (callId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('missed_calls')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', callId);

      if (!error) {
        await fetchMissedCalls(true);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (callId: string) => {
    if (!await window.appConfirm('Bu çağrıyı silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('missed_calls')
        .delete()
        .eq('id', callId);

      if (error) throw error;
      await fetchMissedCalls(true);
    } catch (error) {
      console.error('Error deleting call:', error);
      alert('Çağrı silinirken hata oluştu.');
    }
  };

  const formatPhoneNumber = (number: string) => {
    return karelApiService.formatPhoneNumber(number);
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;

    return format(date, 'dd MMM yyyy HH:mm', { locale: tr });
  };

  const statusOptions = [
    { value: 'pending', label: 'Beklemede', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
    { value: 'called', label: 'Arandı', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
    { value: 'message_sent', label: 'Mesaj İletildi', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400' },
    { value: 'unreachable', label: 'Ulaşılamıyor', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' },
    { value: 'called_back', label: 'Geri Arandı', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
    { value: 'ignored', label: 'Yok Sayıldı', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400' },
    { value: 'converted_to_lead', label: 'Lead Oluşturuldu', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' },
    { value: 'duplicate', label: 'Kopya', color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' },
  ];

  const getStatusColor = (status: string) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption?.color || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  };



  const stats = {
    total: missedCalls.length,
    pending: missedCalls.filter(c => c.status === 'pending').length,
    calledBack: missedCalls.filter(c => c.status === 'called_back').length,
    withLead: missedCalls.filter(c => c.lead_id).length
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-7xl mx-4 max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-red-500 to-pink-500 text-white">
          <div className="flex items-center space-x-3">
            <PhoneMissed className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Cevapsız Çağrılar</h2>
              <p className="text-red-100 text-sm">Karel API Entegrasyonu</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 bg-gradient-to-b from-red-50 to-white dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Toplam</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <PhoneMissed className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Bekleyen</p>
                  <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Geri Arandı</p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.calledBack}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Lead'e Dönüştü</p>
                  <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-1">{stats.withLead}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Telefon numarası veya isim ile ara..."
                className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <button
              onClick={() => fetchMissedCalls(true)}
              disabled={loading}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors disabled:opacity-50 font-medium"
            >
              <Search className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              <span>Filtrele</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tarih:</span>
              <button
                onClick={() => { applyQuickFilter('today'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${quickFilter === 'today'
                  ? 'bg-red-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                Bugün
              </button>
              <button
                onClick={() => { applyQuickFilter('yesterday'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${quickFilter === 'yesterday'
                  ? 'bg-red-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                Dün
              </button>
              <button
                onClick={() => { applyQuickFilter('7days'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${quickFilter === '7days'
                  ? 'bg-red-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                Son 7 Gün
              </button>
            </div>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              <Filter className="w-4 h-4" />
              <span>{showAdvancedFilters ? 'Gelişmiş Filtreleri Gizle' : 'Gelişmiş Filtreler'}</span>
            </button>

            <div className="ml-auto flex items-center gap-2">
              <select
                value={syncPeriod}
                onChange={(e) => setSyncPeriod(e.target.value as '24h' | '7d' | '30d' | '90d')}
                className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="24h">Son 24 saat</option>
                <option value="7d">Son 7 gün</option>
                <option value="30d">Son 30 gün</option>
                <option value="90d">Son 90 gün</option>
              </select>

              <button
                onClick={() => handleSync(true)}
                disabled={syncing || loading}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                title="Sadece geri aranmamış çağrıları getirir"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                <span>Senkronize Et</span>
              </button>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Başlangıç Zamanı
                  </label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setQuickFilter('custom'); }}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Bitiş Zamanı
                  </label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setQuickFilter('custom'); }}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Diyagram Adı (Şube)
                  </label>
                  <select
                    value={diagramName}
                    onChange={(e) => setDiagramName(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tümü</option>
                    {availableDiagrams.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Çağrılar yükleniyor...</p>
              </div>
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-20">
              <PhoneMissed className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {searchQuery ? 'Arama sonucu bulunamadı' : 'Cevapsız çağrı bulunamadı'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                {searchQuery
                  ? `"${searchQuery}" için sonuç bulunamadı. Farklı bir arama terimi deneyin.`
                  : 'Seçili tarih aralığında cevapsız çağrı yok. Farklı bir tarih aralığı seçin veya Karel API\'den veri senkronize edin.'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => handleSync(false)}
                  disabled={syncing}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl transition-colors disabled:opacity-50 font-medium inline-flex items-center space-x-2"
                >
                  <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                  <span>{syncing ? 'Senkronize Ediliyor...' : 'Çağrıları Senkronize Et'}</span>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredCalls.map((call) => (
                <div key={call.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                        <PhoneMissed className="w-7 h-7 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {call.leads ? `${call.leads.first_name} ${call.leads.last_name}` : 'Bilinmeyen Arayan'}
                          </h4>
                          <select
                            value={call.status}
                            onChange={(e) => handleUpdateStatus(call.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${getStatusColor(call.status)}`}
                          >
                            {statusOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {call.lead_id && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                              <UserPlus className="w-3 h-3 mr-1" />
                              Lead
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-4 h-4" />
                            <span className="font-mono font-medium">{formatPhoneNumber(call.call_phone_number)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            <span>{format(new Date(call.call_start_date), 'dd MMM yyyy HH:mm', { locale: tr })}</span>
                          </div>
                          {call.diagram_name && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                {call.diagram_name}
                              </span>
                            </div>
                          )}
                        </div>

                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {getTimeAgo(call.call_start_date)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {call.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleCallBack(call.id, call.call_phone_number)}
                            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-sm"
                          >
                            <PhoneCall className="w-4 h-4" />
                            <span>Geri Ara</span>
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(call.id, 'ignored')}
                            className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                            title="Yok Say"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {call.status === 'called_back' && (
                        <button
                          onClick={() => handleConvertToLead(call)}
                          className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-sm"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>Lead'e Dönüştür</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(call.id)}
                        className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="mt-6 flex justify-center pb-4">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-xl transition-colors disabled:opacity-50 font-medium shadow-sm"
                  >
                    <span>Daha Fazla Yükle</span>
                    {loadingMore && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissedCallsModal;
